/**
 * Eigen benannte Frist am sichtbaren Akt.
 * Zusätzlich zu Berufung/Revision/Rekurs — ersetzt die Freigabe-Karte nicht.
 * Dieselbe Collection `portal_fristen`, kein zweites Erinnerungssystem.
 */

export const PORTAL_EIGEN_FRIST_REGEL_ID = 'eigen_benannt_v1' as const;

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

export type EigenFristDraft = {
  akteId?: string | null;
  name?: string | null;
  datum?: string | null;
  erinnerung?: string | null;
};

export type EigenFristPlan =
  | {
      ok: true;
      akteId: string;
      name: string;
      datum: string;
      erinnerung: string;
      endeDatum: string;
      regelId: typeof PORTAL_EIGEN_FRIST_REGEL_ID;
    }
  | { ok: false; error: string };

/** Fail-closed: ohne Namen oder ohne Datum kein Plan und damit kein Write. */
export function planEigenBenannteFrist(input: EigenFristDraft): EigenFristPlan {
  const akteId = input.akteId?.trim() ?? '';
  const name = input.name?.trim() ?? '';
  const datum = input.datum?.trim() ?? '';
  const erinnerung = input.erinnerung?.trim() ?? '';
  if (!akteId) {
    return { ok: false, error: 'Keine Akte — die Frist wurde nicht gespeichert.' };
  }
  if (!name) {
    return { ok: false, error: 'Ohne Namen wird nicht gespeichert.' };
  }
  if (name.length > 200) {
    return { ok: false, error: 'Der Name ist zu lang — die Frist wurde nicht gespeichert.' };
  }
  if (!ISO_DAY.test(datum)) {
    return { ok: false, error: 'Ohne Datum wird nicht gespeichert.' };
  }
  if (erinnerung && !ISO_DAY.test(erinnerung)) {
    return { ok: false, error: 'Die Erinnerung braucht ein Datum — die Frist wurde nicht gespeichert.' };
  }
  const endeDatum = erinnerung || datum;
  return {
    ok: true,
    akteId,
    name,
    datum,
    erinnerung: endeDatum,
    endeDatum,
    regelId: PORTAL_EIGEN_FRIST_REGEL_ID,
  };
}

export type EigenFristRowRef = {
  id?: string | null;
  akte_id?: string | null;
  regel_id?: string | null;
  frist_typ?: string | null;
};

export function eigenFristReadBackOk(
  rows: EigenFristRowRef[],
  akteId: string,
  name: string,
  recordId: string,
): boolean {
  const aid = akteId.trim();
  const label = name.trim();
  const id = recordId.trim();
  if (!aid || !label || !id) return false;
  return rows.some(
    (row) =>
      (row.id || '').trim() === id &&
      (row.akte_id || '').trim() === aid &&
      (row.regel_id || '').trim() === PORTAL_EIGEN_FRIST_REGEL_ID &&
      (row.frist_typ || '').trim() === label,
  );
}

/** Neu angelegt ohne dieses Zeichen: scharf. Vorbereitet zählt nicht als nächste Frist. */
export function eigenFristKalenderTrace(datum: string, ende: string, scharf: boolean): string {
  const day = datum.trim().slice(0, 10);
  const end = ende.trim().slice(0, 10);
  const base = !end || end === day ? 'Erinnerung am Fristtag' : `Erinnerung ${end}`;
  return scharf ? base : `vorbereitet · ${base}`;
}

export function eigenFristIsPrepared(trace?: string | null): boolean {
  return /^\s*vorbereitet\b/i.test(trace || '');
}

/** Nur das Zeichen davor. Der gespeicherte Text der Frist bleibt. */
export function eigenFristTraceScharf(trace?: string | null, scharf?: boolean): string {
  const base = (trace || '').replace(/^\s*vorbereitet\s*·\s*/i, '').trim();
  if (scharf !== false) return base;
  return base ? `vorbereitet · ${base}` : 'vorbereitet';
}

export function eigenFristErinnerungDraft(start?: string | null, ende?: string | null): string {
  const day = (start || '').trim().slice(0, 10);
  const end = (ende || '').trim().slice(0, 10);
  if (!ISO_DAY.test(end) || end === day) return '';
  return end;
}

export type EigenFristAkteRow = {
  id: string;
  akteId: string;
  name: string;
  datum: string;
  ende: string;
  scharf: boolean;
  regelId: string;
};

/** Änderung nur an der eigenen Frist dieser Akte. Eine andere Akte bleibt stehen. */
export function eigenFristAfterEdit(
  rows: readonly EigenFristAkteRow[],
  akteId: string,
  recordId: string,
  next: { name: string; datum: string; ende: string; scharf: boolean },
): EigenFristAkteRow[] {
  const aid = akteId.trim();
  const id = recordId.trim();
  return rows.map((row) => {
    if (row.id !== id || row.akteId !== aid || row.regelId !== PORTAL_EIGEN_FRIST_REGEL_ID) return row;
    return { ...row, name: next.name, datum: next.datum, ende: next.ende, scharf: next.scharf };
  });
}

/** Löschen nimmt nur diesen Datensatz dieser Akte aus der Liste. */
export function eigenFristAfterDelete(
  rows: readonly EigenFristAkteRow[],
  akteId: string,
  recordId: string,
): EigenFristAkteRow[] {
  const aid = akteId.trim();
  const id = recordId.trim();
  return rows.filter((row) => !(row.id === id && row.akteId === aid));
}

/** Dieselbe Id darf in einer anderen Akte nicht als Treffer gelten. */
export function eigenFristAbsentFromAkte(
  rows: EigenFristRowRef[],
  otherAkteId: string,
  recordId: string,
): boolean {
  const aid = otherAkteId.trim();
  const id = recordId.trim();
  if (!aid || !id) return false;
  return !rows.some(
    (row) => (row.id || '').trim() === id && (row.akte_id || '').trim() === aid,
  );
}
